import { create } from 'zustand';
import { Session } from '@ory/client';
import { devtools, persist } from 'zustand/middleware';
import { UserProfileDTO } from '@/api/auth/index.ts';
import { jwtDecode } from 'jwt-decode';
import { JwtWithClaims } from '@/helpers/auth';
import { DepartmentAccessDTO } from '@/api/trafikportalen';
import * as Sentry from '@sentry/react';

// Function to fetch department access - defined outside the store to avoid circular dependencies
let fetchDepartmentAccess: (() => Promise<DepartmentAccessDTO>) | null = null;

export const setDepartmentAccessFetcher = (
  fetcher: () => Promise<DepartmentAccessDTO>,
) => {
  fetchDepartmentAccess = fetcher;
};

export const refreshDepartmentAccess = async () => {
  if (fetchDepartmentAccess) {
    try {
      const departmentAccess = await fetchDepartmentAccess();
      useAuth.getState().setDepartmentAccess(departmentAccess);
    } catch (error) {
      console.error('Failed to refresh department access:', error);
    }
  }
};

interface AuthStore {
  userProfile: UserProfileDTO | null;
  setUserProfile: (
    userProfile: UserProfileDTO | null,
    previousUserProfile?: UserProfileDTO | null,
  ) => void;

  previousUserProfile: UserProfileDTO | null;

  preImpersonationLocation: string | null;
  setPreImpersonationLocation: (location: string | null) => void;

  accessToken: string | undefined;
  permissions: string[];
  setAccessToken: (accessToken: string | undefined) => void;

  departmentAccess: DepartmentAccessDTO | null;
  setDepartmentAccess: (departmentAccess: DepartmentAccessDTO | null) => void;

  session: Session | undefined;
  setSession: (session: Session | undefined) => void;

  logout: () => void;
}
export const useAuth = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        userProfile: null,
        setUserProfile: (
          newUserProfile,
          previousUserProfile = get().userProfile,
        ) =>
          set({
            userProfile: newUserProfile,
            accessToken: undefined,
            permissions: [],
            previousUserProfile,
            // Clear department access when profile changes
            departmentAccess: null,
          }),

        previousUserProfile: null,
        preImpersonationLocation: null,
        setPreImpersonationLocation: (preImpersonationLocation) =>
          set({ preImpersonationLocation }),

        accessToken: undefined,
        permissions: [],
        setAccessToken: (accessToken) => {
          if (!accessToken) {
            set({
              accessToken: undefined,
              permissions: [],
              departmentAccess: null,
            });
            Sentry.setUser(null);
            return;
          }

          const jwt = jwtDecode<JwtWithClaims>(accessToken);
          set({ accessToken, permissions: jwt.permissions ?? [] });
          Sentry.setUser({
            id: jwt.sub,
            ...jwt,
          });

          if (jwt.organization_id && fetchDepartmentAccess) {
            fetchDepartmentAccess()
              .then((departmentAccess) => {
                // Only update if we still have the same access token (avoid race conditions)
                const currentState = get();
                if (currentState.accessToken === accessToken) {
                  set({ departmentAccess });
                }
              })
              .catch((error) => {
                console.error('Failed to fetch department access:', error);
              });
          } else {
            set({ departmentAccess: null });
          }
        },

        departmentAccess: null,
        setDepartmentAccess: (departmentAccess) => set({ departmentAccess }),

        session: undefined,
        setSession: (session) => set({ session }),

        logout: () => {
          set({
            userProfile: null,
            accessToken: undefined,
            permissions: [],
            session: undefined,
            departmentAccess: null,
            preImpersonationLocation: null,
          });
          Sentry.setUser(null);
        },
      }),
      {
        name: 'auth',
      },
    ),
  ),
);
